import { useMemo, useSyncExternalStore } from 'react';
import { ChevronLeft, ChevronRight } from '../../icons';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { calDateKey, isSameCalDay } from '../../lib/calendar-shell';

interface CalendarMiniMonthProps {
  /** 표시 월(그 달의 아무 날짜나 가능 — 내부에서 1일로 정규화). */
  month: Date;
  /** 선택일(우측 레일이 보여주는 날짜). */
  selected: Date;
  /** 일정이 있는 날(YYYY-MM-DD) — 점 표시용. */
  eventDays: ReadonlySet<string>;
  onSelect: (date: Date) => void;
  /** 월 이동(-1/+1). 본체 그리드의 cursor 와 같은 navigate 를 재사용한다. */
  onNavigate: (delta: number) => void;
}

/**
 * CalendarMiniMonth — 사이드바 상단 미니 캘린더 (레이아웃 참조 all-frame.html L1132~1135).
 *
 * 본체 월 그리드와 같은 cursor 를 보므로 월 이동이 양쪽에 동시 반영된다.
 * 일정이 있는 날은 점(dot)만 찍고 제목은 싣지 않는다 — 밀도를 낮춰 날짜 선택 기능에 집중.
 */
export function CalendarMiniMonth({ month, selected, eventDays, onSelect, onNavigate }: CalendarMiniMonthProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);

  const monthIdx = month.getMonth();

  // 6주 × 7일 — MonthView 와 동일한 그리드 기준(일요일 시작).
  const days = useMemo(() => {
    const monthStart = new Date(month);
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - monthStart.getDay());
    const out: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [month]);

  const today = new Date();
  const dayNames = [
    tCalendar('calendar.month.dayname.sun'),
    tCalendar('calendar.month.dayname.mon'),
    tCalendar('calendar.month.dayname.tue'),
    tCalendar('calendar.month.dayname.wed'),
    tCalendar('calendar.month.dayname.thu'),
    tCalendar('calendar.month.dayname.fri'),
    tCalendar('calendar.month.dayname.sat'),
  ];

  return (
    <div className="cal-mini" aria-label={tCalendar('calendar.shell.mini_label')}>
      <div className="cal-mini-head">
        <span className="cal-mini-title">
          {month.getFullYear()}. {String(month.getMonth() + 1).padStart(2, '0')}
        </span>
        <div className="cal-mini-nav">
          <button
            type="button"
            onClick={() => onNavigate(-1)}
            aria-label={tCalendar('calendar.nav.prev_month')}
          >
            <ChevronLeft size={13} />
          </button>
          <button
            type="button"
            onClick={() => onNavigate(1)}
            aria-label={tCalendar('calendar.nav.next_month')}
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      <div className="cal-mini-grid">
        {dayNames.map((n, i) => (
          <div key={n} className={`cal-mini-dow${i === 0 ? ' cal-mini-dow--sun' : ''}`}>{n}</div>
        ))}
        {days.map((d) => {
          const key = calDateKey(d);
          const cls = [
            'cal-mini-day',
            d.getMonth() !== monthIdx ? 'cal-mini-day--other' : '',
            d.getDay() === 0 ? 'cal-mini-day--sun' : '',
            eventDays.has(key) ? 'cal-mini-day--has' : '',
            isSameCalDay(d, today) ? 'cal-mini-day--today' : '',
            isSameCalDay(d, selected) ? 'cal-mini-day--sel' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={key}
              type="button"
              className={cls}
              onClick={() => onSelect(d)}
              aria-pressed={isSameCalDay(d, selected)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
